import React, { useCallback, useState } from "react"
import { Button } from "antd"
import { PlusOutlined } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { useSelector } from "react-redux"
import { routes } from "router/routes"
import { NewConfirmModal } from "ui/NewConfirmModal"
import { PageName } from "ui/PageName"
import { Text } from "ui/Typography"
import { buildBlackoutReservationsLink, openReservationsLink } from "helpers/blackouts"
import BlackoutCreatedModal from "ui/Blackout/BlackoutCreatedModal"
import useActiveGuide from "hooks/useActiveClient"
import { useDevice } from "hooks/useDevice"
import BlackoutSectionCard from "./components/BlackoutSectionCard"
import BlackoutRow from "./components/BlackoutRow"
import ResourcesBlackoutCard from "./components/ResourcesBlackoutCard"
import BlackoutFilters from "./components/BlackoutFilters"
import BlackoutFormModal from "./modals/BlackoutFormModal"
import { useBlackouts } from "./hooks/useBlackouts"
import { useBlackoutFilterOptions } from "./hooks/useBlackoutFilterOptions"
import { BLACKOUT_SCOPES } from "./constants"
import styles from "./index.module.css"
import DeleteBlackoutMessage from "ui/Blackout/DeleteBlackoutMessage"

/** A stable empty fallback, so the Review callbacks are not rebuilt on every render. */
const NO_TARGETS = []

export const BlackoutSettings = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { isActive, isPending } = useActiveGuide()
  const { isMobile } = useDevice()
  const isDisabled = !isActive || isPending

  const {
    items,
    byScope,
    isLoading,
    isSaving,
    isDeleting,
    formState,
    openAddModal,
    closeFormModal,
    handleSubmit,
    blackoutToDelete,
    setBlackoutToDelete,
    handleDelete,
    getMenuItems,
    activeResourceTab,
    setActiveResourceTab,
    filters,
    updateFilters,
    clearFilters
  } = useBlackouts({ enabled: true })

  const { resourceGroups, listingGroups } = useBlackoutFilterOptions({ enabled: true })
  // Warmed by the filter options above; lets an equipment Review filter by equipment, not asset.
  const equipmentTargets = useSelector((state) => state.blackouts?.targets?.data?.[BLACKOUT_SCOPES.EQUIPMENT] ?? NO_TARGETS)

  // The dedicated conflict-review screen is not built yet; send the guide to the reservations
  // list, narrowed to the blackout's own dates and listings, so the warning is actionable. A
  // record with no dated entry has nothing to narrow by and opens the list as it comes.
  // `null` until a save lands on bookings that need the guide's attention; then the receipt's
  // count, whether it was an edit, and the pre-filtered list to hand over.
  const [saveResult, setSaveResult] = useState(null)

  /**
   * Saving is not the end of the story when the blackout lands on live bookings: it takes effect
   * immediately, but each clashing booking still has to be cancelled or moved by hand. So a save
   * that hits any reports what is left to do (Figma 7476:151792) instead of just closing.
   */
  const submitBlackout = useCallback(
    async (payload, impact) => {
      // `formState` is cleared by a successful save, so what the modal was doing is read first.
      const isEdit = Boolean(formState?.blackout)
      const count = impact?.conflicting_bookings_count || 0
      const saved = await handleSubmit(payload)
      if (!saved || !count) return
      setSaveResult({
        count,
        isEdit,
        link: buildBlackoutReservationsLink(
          {
            // A new record's id comes back with the save. Without it the link falls back to a date
            // window, which cannot express a repeat's days and lists every booking up to its end.
            id: formState?.blackout?.id ?? saved.id,
            scope: payload.scope,
            entries: payload.entries,
            recurrence: payload.recurrence,
            target_ids: payload.target_ids,
            impact
          },
          equipmentTargets
        )
      })
    },
    [formState, handleSubmit, equipmentTargets]
  )

  /** The design opens this in a new tab: the guide is mid-flow here and should not lose the page. */
  const openSavedBookings = useCallback(() => {
    openReservationsLink(saveResult?.link)
    setSaveResult(null)
  }, [saveResult])

  // A new tab, as the row's "Review ↗" promises — the guide is checking a conflict, not leaving
  // the settings page they are working through.
  const handleReviewConflicts = useCallback(
    (blackout) => openReservationsLink(buildBlackoutReservationsLink(blackout, equipmentTargets)),
    [equipmentTargets]
  )

  const hasActiveFilters = Boolean(filters.dateRange) || filters.resourceIds.length > 0 || filters.listingIds.length > 0

  /**
   * Singular and plural are picked by hand: the project is on i18next 19 with the v3 JSON
   * format, where neither plural convention already in `en.json` resolves on its own.
   */
  const resultsText =
    items.length === 1
      ? t("settings.blackout.results_count_single", { defaultValue: "1 result" })
      : t("settings.blackout.results_count", { defaultValue: "{{count}} results", count: items.length })

  const businessBlackouts = byScope[BLACKOUT_SCOPES.BUSINESS]
  const listingBlackouts = byScope[BLACKOUT_SCOPES.LISTING]
  const resourceCount = byScope[BLACKOUT_SCOPES.CREW].length + byScope[BLACKOUT_SCOPES.EQUIPMENT].length

  /** "Business Blackout (2)" — the count is part of the heading in the design. */
  const withCount = (label, count) => `${label} (${count})`

  const renderAddButton = (scope) => (
    <Button
      color="primary"
      variant="outlined"
      shape="round"
      icon={<PlusOutlined />}
      onClick={isDisabled ? undefined : () => openAddModal(scope)}
      disabled={isDisabled}
      aria-disabled={isDisabled}
    >
      {t("settings.blackout.add", { defaultValue: "Add" })}
    </Button>
  )

  const calendarButton = (
    <Button color="primary" variant="solid" shape="round" className={styles.calendarButton} onClick={() => navigate(routes.proCalendarPath())}>
      {t("settings.blackout.view_in_calendar", { defaultValue: "View in Calendar" })}
    </Button>
  )

  return (
    <div className={styles.page}>
      {/* This page renders its own `PageName` (ProSettingsLayout opts out via
          `RENDERS_OWN_PAGE_NAME`) so "View in Calendar" can sit on the title line. On a phone
          it shares the filter row instead, as the mobile design shows — beside the title there
          it would wrap "Blackout Settings" onto two lines. */}
      <PageName
        title={t("settings.blackout.page_title", { defaultValue: "Blackout Settings" })}
        backPath={routes.proSettingsRootPath()}
        actions={isMobile ? null : calendarButton}
      />

      <div className={styles.toolbarBlock}>
        <div className={styles.toolbar}>
          <div className={styles.filters}>
            <BlackoutFilters
              filters={filters}
              onChange={updateFilters}
              onClear={clearFilters}
              resourceGroups={resourceGroups}
              listingGroups={listingGroups}
            />
          </div>
          {isMobile && calendarButton}
        </div>

        {/* Only a narrowed list says how much it is showing — unfiltered, the section headings
            already carry their own counts. */}
        {hasActiveFilters && !isLoading && (
          <Text variant="caption" size="lg" className={styles.resultsCount}>
            {resultsText}
          </Text>
        )}
      </div>

      <BlackoutSectionCard
        title={withCount(t("settings.blackout.business_title", { defaultValue: "Business Blackout" }), businessBlackouts.length)}
        description={t("settings.blackout.business_description", {
          defaultValue: "Applies to all listings, crew and equipment."
        })}
        action={renderAddButton(BLACKOUT_SCOPES.BUSINESS)}
        isLoading={isLoading}
        isEmpty={!businessBlackouts.length}
        emptyText={t("settings.blackout.empty_business", { defaultValue: "No business blackouts yet" })}
      >
        {businessBlackouts.map((blackout) => (
          <BlackoutRow key={blackout.id} blackout={blackout} menuItems={getMenuItems(blackout)} onReviewConflicts={handleReviewConflicts} />
        ))}
      </BlackoutSectionCard>

      {/* Desktop puts Listing and Resources side by side (Figma 6249:112140); below 1025px the
          row becomes a column, as the tablet and phone frames show. */}
      <div className={styles.sectionRow}>
        <BlackoutSectionCard
          variant="surface"
          title={withCount(t("settings.blackout.listing_title", { defaultValue: "Listing Blackout" }), listingBlackouts.length)}
          description={t("settings.blackout.listing_description", { defaultValue: "Selected listings are unavailable during this period" })}
          action={renderAddButton(BLACKOUT_SCOPES.LISTING)}
          isLoading={isLoading}
          isEmpty={!listingBlackouts.length}
          emptyText={t("settings.blackout.empty_listing", { defaultValue: "No listing blackouts yet" })}
        >
          {listingBlackouts.map((blackout) => (
            <BlackoutRow key={blackout.id} variant="card" blackout={blackout} menuItems={getMenuItems(blackout)} onReviewConflicts={handleReviewConflicts} />
          ))}
        </BlackoutSectionCard>

        <ResourcesBlackoutCard
          crewBlackouts={byScope[BLACKOUT_SCOPES.CREW]}
          equipmentBlackouts={byScope[BLACKOUT_SCOPES.EQUIPMENT]}
          totalCount={resourceCount}
          activeTab={activeResourceTab}
          onTabChange={setActiveResourceTab}
          isLoading={isLoading}
          isDisabled={isDisabled}
          onAdd={openAddModal}
          getMenuItems={getMenuItems}
          onReviewConflicts={handleReviewConflicts}
        />
      </div>

      {formState && (
        <BlackoutFormModal
          isOpen
          scope={formState.scope}
          blackout={formState.blackout}
          onCancel={closeFormModal}
          onSubmit={submitBlackout}
          isSaving={isSaving}
        />
      )}

      {saveResult && (
        <BlackoutCreatedModal
          isOpen
          count={saveResult.count}
          isEdit={saveResult.isEdit}
          onClose={() => setSaveResult(null)}
          onReviewBookings={openSavedBookings}
        />
      )}

      <NewConfirmModal
        visible={!!blackoutToDelete}
        onClose={() => setBlackoutToDelete(null)}
        onConfirm={handleDelete}
        title={t("settings.blackout.delete_title", { defaultValue: "Delete Blackout?" })}
        message={<DeleteBlackoutMessage blackout={blackoutToDelete} />}
        warningText={t("settings.blackout.delete_warning", { defaultValue: "All related restrictions will be removed immediately." })}
        hasIcon={false}
        confirmLabel={t("settings.action_delete", { defaultValue: "Delete" })}
        loading={isDeleting}
      />
    </div>
  )
}

export default BlackoutSettings
